/**
 * Title + description block rendered at the top of the dynamic
 * exception page. The active config's `pageHeader` overrides take
 * precedence over the canonical exception type record - useful for
 * "rollup" views (e.g. sales-compare base code) whose name reads
 * misleadingly under their broadened scope.
 */
import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { ExceptionDomainIcon } from '../../SystemIcons'
import ExceptionTypeLabel from '../../shared/ExceptionTypeLabel'
import type { ExceptionConfig } from '../../../config/exceptions/types'
import { ICON_TILE_BG } from '../../../theme/exceptionTableStyles'

interface ExceptionPageHeaderProps {
  /** Domain key for the leading icon tile. Omit to render no icon (unless
   * `icon` is supplied). */
  domain?: string
  /** Custom leading icon node; overrides the domain icon when provided
   * (e.g. the hub's fixed "Exception Reports" header). */
  icon?: React.ReactNode
  title: string
  description?: string
  rightAdornment?: React.ReactNode
  titleAdornment?: React.ReactNode
  /** Business classification code (process_issue / data_discrepancy). When set,
   * the type label leads the description line as a subtle inline lead-in. */
  exceptionType?: string | null
}

export const resolvePageHeader = (
  config: ExceptionConfig | null,
  exceptionType: { name: string; overview?: string },
): { title: string; description?: string } => ({
  title: config?.pageHeader?.title ?? exceptionType.name,
  description: config?.pageHeader?.description ?? exceptionType.overview,
})

const ExceptionPageHeader: React.FC<ExceptionPageHeaderProps> = ({
  domain,
  icon,
  title,
  description,
  rightAdornment,
  titleAdornment,
  exceptionType,
}) => {
  return (
    <Box mb={3}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: description ? 0.5 : 0 }}
      >
        {/* Title row wraps on narrow viewports so the type + visibility chips
            drop to a line beneath the title rather than crowding it. */}
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          spacing={1.5}
          rowGap={1}
        >
          {(icon || domain) && (
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                backgroundColor: ICON_TILE_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon ?? (
                <ExceptionDomainIcon domain={domain!} variant="outlined" size={22} applyColor />
              )}
            </Box>
          )}
          <Typography variant="h4">{title}</Typography>
          {/* Type classification chip sits between the title and the
              visibility (PSC Only) chip - same shared mapping as the report
              strip + row markers. The `top` nudge matches the PSC Only chip
              so both baselines line up against the h4 title. */}
          {exceptionType && (
            <ExceptionTypeLabel
              variant="pill"
              exceptionType={exceptionType}
              iconSize={15}
              sx={{ position: 'relative', top: '3px' }}
            />
          )}
          {titleAdornment}
        </Stack>
        {rightAdornment && (
          <Box sx={{ flexShrink: 0 }}>{rightAdornment}</Box>
        )}
      </Stack>
      {description && (
        <ExceptionTypeLabel
          description={description}
          iconSize={16}
          descriptionVariant="body1"
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  )
}

export default ExceptionPageHeader
